import { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ChainVisualization from '@/components/ChainVisualization';
import { API } from '@/config/api';

export default function DeletedDocumentsPage({ token, user, onLogout }) {
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chainDialogOpen, setChainDialogOpen] = useState(false);
  const [selectedChain, setSelectedChain] = useState([]);
  const [selectedUdtId, setSelectedUdtId] = useState('');

  useEffect(() => {
    fetchDeletedDocuments();
  }, []);

  const fetchDeletedDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedDocuments(response.data.deleted_documents || []);
    } catch (error) {
      console.error('Failed to fetch deleted documents', error);
    } finally {
      setLoading(false);
    }
  };

  const openChainDialog = (doc) => {
    if (!doc?.watermark_chain || doc.watermark_chain.length === 0) return;
    setSelectedChain(doc.watermark_chain);
    setSelectedUdtId(doc.udt_id);
    setChainDialogOpen(true);
  };

  return (
    <DashboardLayout user={user} token={token} onLogout={onLogout} stats={{}}>
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto w-full min-w-0">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Deleted Documents
            </h1>
            <p className="text-gray-600 text-sm">
              View read-only history of documents you have deleted.
            </p>
          </div>
          {deletedDocuments.length > 0 && (
            <Badge style={{ background: '#dc2626' }}>
              {deletedDocuments.length} {deletedDocuments.length === 1 ? 'Entry' : 'Entries'}
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-gray-600 text-sm">Loading deleted documents...</p>
          </div>
        ) : deletedDocuments.length === 0 ? (
          <div className="text-center py-12">
            <Trash2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2" style={{ color: '#666' }}>
              No deleted documents yet
            </p>
            <p className="text-sm" style={{ color: '#999' }}>
              When you delete a document, a history entry will appear here.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {deletedDocuments.map((doc) => (
              <Card
                key={`${doc.udt_id}-${doc.deleted_at}`}
                className="flex flex-col border border-red-100 bg-red-50/80 shadow-sm hover:shadow-md transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-8 h-8" style={{ color: '#dc2626' }} />
                      <Badge style={{ background: '#dc2626', fontSize: '10px' }}>Deleted</Badge>
                    </div>
                  </div>
                  <CardTitle className="text-base mt-2 text-gray-900">
                    {doc.file_name || 'Unknown file'}
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-600">
                    UDT ID: {doc.udt_id}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-1 text-xs text-gray-700">
                  {doc.purpose && (
                    <p>
                      <span className="font-semibold">Purpose:</span> {doc.purpose}
                    </p>
                  )}
                  {doc.file_type && (
                    <p>
                      <span className="font-semibold">Type:</span> {doc.file_type}
                    </p>
                  )}
                  {doc.created_at && (
                    <p>
                      <span className="font-semibold">Created:</span>{' '}
                      {new Date(doc.created_at).toLocaleString()}
                    </p>
                  )}
                  {doc.deleted_at && (
                    <p>
                      <span className="font-semibold">Deleted:</span>{' '}
                      {new Date(doc.deleted_at).toLocaleString()}
                    </p>
                  )}
                  <div className="pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!doc.watermark_chain || doc.watermark_chain.length === 0}
                      onClick={() => openChainDialog(doc)}
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Custody Chain
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Custody Chain Dialog */}
      <Dialog open={chainDialogOpen} onOpenChange={setChainDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-base sm:text-lg">Document Custody Chain</DialogTitle>
          </DialogHeader>
          <ChainVisualization chain={selectedChain} udtId={selectedUdtId} isDeletedView />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
